import { Box, Button, Typography } from "@mui/material";
import { observer } from "mobx-react";
import { FC } from "react";

import EyeIcon from "../../icons/EyeIcon";
import ChevronLeftIcon from "../../icons/ChevronLeftIcon";

import { useChatStore } from "../../../hooks/useChatStore";
import { useAppStore } from "../../../hooks/useAppStore";
import Row from "../../ui/core/Row";
import { useChatCategoryStore } from "../../../hooks/useChatCategoryStore";

/**
 * Fixed policy summary bar shown above the chat input for Payer Policy Questions
 * chats (when the policy preview panel is closed). "Open Policy" reopens the same
 * policy in the side panel. Mirrors ContractSummaryBar's role for contract chats.
 */
const PolicySummaryBar: FC<{ hideBack?: boolean }> = observer(({ hideBack }) => {
  const chatStore = useChatStore();
  const { policyToPreview, setPolicyToPreview } = useAppStore();
  const { setIsPoliciesDocumentsDrawerOpen, policyResearchData } = useChatCategoryStore();

  const policyPreviewForChat = chatStore.policyPreviewForChat;

  // policyToPreview is global and outlives the chat that set it, so only treat the
  // side panel as "already showing this document" when it refers to the same policy.
  const isSameDocument =
    !!policyPreviewForChat &&
    policyToPreview?.document_id === policyPreviewForChat.document_id;

  const handleOpenPolicy = () => {
    if (!policyPreviewForChat) return;
    // Message metadata doesn't carry source_url (see ChatStore.policyPreviewForChat), so
    // prefer the full row from the research table -- if it's loaded -- over the bare
    // document_id/title pair, otherwise PolicyTypeBadge/"open in new tab" degrade.
    const fullPolicy = policyResearchData.policies.find(
      (policy) => policy.document_id === policyPreviewForChat.document_id
    );
    setPolicyToPreview(fullPolicy || policyPreviewForChat);
    setIsPoliciesDocumentsDrawerOpen(true);
  };

  // The policy preview side panel already shows this document when open
  if (isSameDocument) {
    return null;
  }

  if (!policyPreviewForChat) {
    return null;
  }

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        width: "100%",
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: "white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Row
          sx={{
            alignItems: "center",
            gap: 1,
            flex: 1,
            minWidth: 0,
            flexWrap: "nowrap",
          }}
        >
          {hideBack ? null : (
            <ChevronLeftIcon className="w-4 h-4 shrink-0 text-gray-500" />
          )}

          <Typography fontWeight={600} noWrap sx={{ minWidth: 0 }}>
            {policyPreviewForChat.title || policyPreviewForChat.document_id}
          </Typography>
        </Row>

        <Button
          variant="contained"
          color="primary"
          size="small"
          endIcon={<EyeIcon width={16} height={16} />}
          onClick={handleOpenPolicy}
          sx={{ flexShrink: 0, borderRadius: "999px", px: 2 }}
        >
          Open Policy
        </Button>
      </Box>
    </Box>
  );
});

export default PolicySummaryBar;
